/**
 * @jsx jsx
 */
import React, { useCallback, useState } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { cssMap as atlaskitCssMap, type StrictXCSSProp } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';

import { analyticsAttributes, withAnalyticsContextData } from '../../common/utils/analytics';
import { FadeIn } from './fade-in';

type SectionProps = {
	children?: React.ReactNode;
	hasDivider?: boolean;
	isCollapsible?: boolean;
	isUnitsRevampEnabled?: boolean;
	sectionId: string;
	tag?: keyof JSX.IntrinsicElements;
	title?: React.ReactNode;
	xcss?: StrictXCSSProp<'width' | 'borderTop', never>;
};

type SectionAnalyticsContext = {
	attributes: {
		group: string;
		groupItemsCount: number;
	};
};

const styles = cssMap({
	sectionBase: {
		paddingTop: token('space.100'),
		paddingRight: 0,
		paddingBottom: token('space.075'),
		paddingLeft: 0,
		overflow: 'hidden',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginTop: '-1px',
	},
	sectionRevampSpacing: {
		paddingBottom: token('space.100'),
	},
	divider: {
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
	},
	list: {
		listStyleType: 'none',
		padding: 0,
		margin: 0,
	},
	box: {
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		paddingTop: '0',
		paddingBottom: token('space.050'),
	},
	titleBase: {
		marginTop: token('space.075'),
		marginLeft: token('space.100'),
		marginBottom: token('space.075'),
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		color: token('color.text.subtlest'),
		fontWeight: token('font.weight.bold'),
	},
	// Chevron wrapper — `space.075` padding around a 12px glyph, per the Figma spec.
	chevron: {
		display: 'flex',
		paddingTop: token('space.075'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.075'),
		justifyContent: 'center',
		alignItems: 'center',
	},
});

const triggerStyles = atlaskitCssMap({
	root: {
		display: 'flex',
		height: '32px',
		alignItems: 'center',
		alignSelf: 'stretch',
		gap: token('space.150'),
		width: '100%',
		paddingTop: token('space.050'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.100'),
		borderRadius: token('radius.medium'),
		backgroundColor: token('color.background.neutral.subtle'),
	},
	titleBase: {
		font: token('font.heading.xxsmall'),
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		color: token('color.text.subtlest'),
		fontWeight: token('font.weight.bold'),
		fontStyle: 'normal',
	},
});

type CollapsibleSectionHeaderProps = {
	isCollapsed: boolean;
	listId: string;
	onToggle: () => void;
	sectionId: string;
	title: React.ReactNode;
};

/**
 * The collapsible section header. Kept as its own component so the default
 * (non-collapsible) `SectionContainer` path does not depend on an
 * `IntlProvider` being present in the ancestry.
 */
const CollapsibleSectionHeader = ({
	isCollapsed,
	listId,
	onToggle,
	sectionId,
	title,
}: CollapsibleSectionHeaderProps) => (
	<Heading as="h2" size="xxsmall">
		<Pressable
			xcss={triggerStyles.root}
			aria-expanded={!isCollapsed}
			aria-controls={listId}
			onClick={onToggle}
			testId={`${sectionId}__section-toggle`}
		>
			<span css={styles.chevron}>
				{isCollapsed ? (
					<ChevronRightIcon label="" color="currentColor" size="small" />
				) : (
					<ChevronDownIcon label="" color="currentColor" size="small" />
				)}
			</span>
			<Box xcss={triggerStyles.titleBase}>{title}</Box>
		</Pressable>
	</Heading>
);

const SectionContainerComponent = ({
	sectionId,
	title,
	children,
	xcss,
	tag: Tag = 'ul',
	isCollapsible = false,
	hasDivider = true,
	isUnitsRevampEnabled = false,
}: SectionProps) => {
	const [isCollapsed, setIsCollapsed] = useState(false);

	const handleToggle = useCallback(() => setIsCollapsed((prev) => !prev), []);

	const hasChildren = React.Children.toArray(children).some(Boolean);
	if (!hasChildren) {
		return null;
	}

	const listId = `${sectionId}__section-list`;
	const showCollapsibleHeader = isCollapsible && Boolean(title);

	return (
		<section
			css={[
				styles.sectionBase,
				isUnitsRevampEnabled && styles.sectionRevampSpacing,
				hasDivider && styles.divider,
			]}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- required with `xcss`
			className={xcss}
			data-testid={`${sectionId}__section`}
		>
			<div css={styles.box}>
				<FadeIn>
					{showCollapsibleHeader ? (
						<CollapsibleSectionHeader
							isCollapsed={isCollapsed}
							listId={listId}
							onToggle={handleToggle}
							sectionId={sectionId}
							title={title}
						/>
					) : (
						title && (
							<Heading as="h2" size="xxsmall">
								<Box xcss={styles.titleBase}>{title}</Box>
							</Heading>
						)
					)}
				</FadeIn>
				<Tag
					id={listId}
					css={styles.list}
					hidden={showCollapsibleHeader && isCollapsed}
					data-testid={`${sectionId}__section-list`}
				>
					{children}
				</Tag>
			</div>
		</section>
	);
};

export const SectionContainer: React.ComponentType<SectionProps> = withAnalyticsContextData<
	SectionProps,
	SectionAnalyticsContext
>((props) => ({
	...analyticsAttributes({
		group: props.sectionId,
		groupItemsCount: React.Children.toArray(props.children).filter(React.isValidElement).length,
	}),
}))(SectionContainerComponent);
